var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { CheckableAvatarSelectItem, CheckableSelectItem, CheckableTagSelectItem, Select, TagSelectSuggestion, } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import map from 'lodash/fp/map';
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { searchApi, } from 'apis/search';
import { useHasFeature } from 'components/shared/featureFlag';
import { useLazyPaginatedEndpoint, } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { getChannelFeatureFlagsStatus } from 'utils/featureFlagging/channelFeatureFlags';
import { filterItems, } from '../../../../../../utils/definitions';
import { useConditionFieldSelect } from './utils/useConditionFieldSelect';
const defaultItemFormatter = map(({ id, name }) => ({ id, name }));
const tagItemFormatter = map(({ id, name, color }) => ({
    id,
    name,
    color,
}));
const userItemFormatter = map(({ id, display_name, phone_number, email }) => ({
    id,
    name: display_name || email || phone_number || id,
}));
function createFreeTextFilter(field) {
    function Filter(_a) {
        var { condition, onChange } = _a, props = __rest(_a, ["condition", "onChange"]);
        const { handleChange, handleClear, mapValuesToItems } = useConditionFieldSelect({ condition, onChange });
        const [search, setSearch] = useState('');
        const selectedItems = mapValuesToItems();
        const { disabled } = props;
        const emptyMessages = {
            numeric_id: 'Enter the numeric conversation ID (e.g. 123456)',
        };
        // When there is a search input, don't render the empty state.
        const emptyComponent = search ? () => null : undefined;
        /**
         * Handles adding a new ID to the selection. If the ID starts with #, we
         * strip ID. If the ID contains any other characters than digits, we don't
         * add it.
         */
        const handleAddId = (item) => {
            const id = item.id.toString().replace('#', '');
            const isValid = /^[0-9]*$/.test(id);
            if (isValid) {
                handleChange({ id, name: `#${id}` });
            }
            setSearch('');
        };
        return (_jsx(Select, { multiple: true, clearable: true, searchable: true, placeholder: "Type a value and click 'Add'", items: [], selectedItems: selectedItems, onChange: handleAddId, onClear: handleClear, hideSelectedItems: true, limit: 3, disabled: disabled, onSearch: setSearch, suggestionComponent: TagSelectSuggestion, onSuggest: (id) => handleAddId({ id, name: `#${id}` }), renderSuggestion: (id) => `Add "#${id.replace('#', '')}"`, emptyMessage: emptyMessages[field], emptyComponent: emptyComponent }));
    }
    return Filter;
}
function createStaticFilter(field) {
    function Filter(_a) {
        var { condition, onChange } = _a, props = __rest(_a, ["condition", "onChange"]);
        const { handleChange, handleClear, mapValuesToItems } = useConditionFieldSelect({ condition, onChange });
        const hasInstagramChannel = useHasFeature('instagram-management');
        const channelFlags = getChannelFeatureFlagsStatus();
        const items = useMemo(() => {
            return field === 'initial_channel'
                ? filterItems[field].filter(({ id: channel }) => {
                    const casedChannel = convertChannelCasing(channel, 'snake');
                    if (casedChannel === 'facebook_messenger') {
                        // Instagram conversations appear as Facebook conversations
                        return channelFlags.get(casedChannel) || hasInstagramChannel;
                    }
                    return channelFlags.get(casedChannel);
                })
                : filterItems[field];
        }, [hasInstagramChannel, channelFlags]);
        const selectedItems = mapValuesToItems();
        const { placeholder, disabled } = props;
        return (_jsx(Select, { multiple: true, clearable: true, searchable: true, placeholder: placeholder, items: items, selectedItems: selectedItems, onChange: handleChange, onClear: handleClear, hideSelectedItems: true, limit: 3, disabled: disabled }));
    }
    return Filter;
}
function createDynamicFilter({ endpoint: { options, defaultArg }, itemComponent = CheckableSelectItem, itemFormatter, prepareDisabled, }) {
    function Filter({ condition, placeholder, disabled, onChange, }) {
        const [searchTerm, setSearchTerm] = useState();
        const { items: hits, arg, asyncStatus, changeArg, hasNextPage, nextPage, } = useLazyPaginatedEndpoint(Object.assign(Object.assign({}, options), { infinite: true }));
        useEffect(() => {
            if (searchTerm !== (arg === null || arg === void 0 ? void 0 : arg.searchTerm)) {
                changeArg(Object.assign(Object.assign({}, defaultArg), { searchTerm, offset: undefined, limit: undefined }), {
                    debounceTime: 300,
                });
            }
            // Don't rely on arg as dependency because we change the arg inside.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [changeArg, searchTerm]);
        const { handleChange, handleClear, mapValuesToItems } = useConditionFieldSelect({ condition, onChange });
        const handleSearch = useCallback((value) => {
            setSearchTerm(value === null || value === void 0 ? void 0 : value.trim());
        }, []);
        const handleToggle = useCallback((isOpen) => {
            if (isOpen && !hits.length) {
                changeArg(Object.assign(Object.assign({}, defaultArg), { searchTerm, offset: undefined, limit: undefined }), { debounceTime: 0 });
            }
            else if (!isOpen && searchTerm !== '') {
                setSearchTerm('');
            }
        }, [changeArg, hits.length, searchTerm]);
        const items = itemFormatter(hits);
        const selectedItems = useMemo(() => mapValuesToItems(), [mapValuesToItems]);
        return (_jsx(Select, { multiple: true, clearable: true, searchable: true, placeholder: placeholder, onSearch: handleSearch, onChange: handleChange, onClear: handleClear, onToggle: handleToggle, pagination: {
                infinite: true,
                nextEnable: hasNextPage,
                previousEnable: false,
                onNext: () => Promise.resolve(nextPage()),
            }, items: items, selectedItems: selectedItems, disabledItems: prepareDisabled === null || prepareDisabled === void 0 ? void 0 : prepareDisabled(selectedItems, items), itemComponent: itemComponent, loading: asyncStatus === 'pending', limit: 3, disabled: disabled }));
    }
    return Filter;
}
export const ConditionFieldId = createFreeTextFilter('numeric_id');
export const ConditionFieldDirection = createStaticFilter('initial_direction');
export const ConditionFieldStatus = createStaticFilter('status');
export const ConditionFieldChannel = createStaticFilter('initial_channel');
export const ConditionFieldQueue = createDynamicFilter({
    endpoint: {
        options: {
            endpoint: searchApi.endpoints.readSearchQueue,
            itemsSelector: ({ data: { results } }) => { var _a; return (_a = results.hits) !== null && _a !== void 0 ? _a : []; },
        },
        defaultArg: {
            sort: { key: 'name', direction: 'asc' },
        },
    },
    itemComponent: CheckableSelectItem,
    itemFormatter: defaultItemFormatter,
});
export const ConditionFieldContacts = createDynamicFilter({
    endpoint: {
        options: {
            endpoint: searchApi.endpoints.readSearchUser,
            itemsSelector: ({ data: { results } }) => { var _a; return (_a = results.hits) !== null && _a !== void 0 ? _a : []; },
        },
        defaultArg: {
            sort: { key: 'display_name', direction: 'asc' },
            filter: {
                facets: {
                    roles: ['enduser'],
                },
            },
        },
    },
    itemComponent: CheckableAvatarSelectItem,
    itemFormatter: userItemFormatter,
});
export const ConditionFieldAgents = createDynamicFilter({
    endpoint: {
        options: {
            endpoint: searchApi.endpoints.readSearchUser,
            itemsSelector: ({ data: { results } }) => { var _a; return (_a = results.hits) !== null && _a !== void 0 ? _a : []; },
        },
        defaultArg: {
            sort: { key: 'display_name', direction: 'asc' },
            filter: {
                facets: {
                    roles: ['admin', 'systemadmin', 'teamlead', 'agent'],
                },
            },
        },
    },
    itemComponent: CheckableAvatarSelectItem,
    itemFormatter: userItemFormatter,
});
export const ConditionFieldTags = createDynamicFilter({
    endpoint: {
        options: {
            endpoint: searchApi.endpoints.readSearchTag,
            itemsSelector: ({ data: { results } }) => { var _a; return (_a = results.hits) !== null && _a !== void 0 ? _a : []; },
        },
        defaultArg: {
            sort: { key: 'name', direction: 'asc' },
        },
    },
    itemComponent: CheckableTagSelectItem,
    itemFormatter: tagItemFormatter,
    prepareDisabled: (_, tags) => tags.filter((tag) => tag.is_deactivated).map((tag) => tag.id),
});
